import { useEffect, useState } from "react";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { IAttachmentTypeListItem } from "models/attachments";

export interface WorkItemAttachmentType {
    typeId: number;
    typeName: string;
    isSensitive: boolean;
    groupName: string;
    fileRequired?: boolean;
    linkRequired?: boolean;
}

type WorkItemAttachmentsTypeResult = [
    IAttachmentTypeListItem[] | null,
    boolean
];

const makeAttachmentTypeListUrl = (workItemId: number, draft?: boolean) => draft
    ? `/api/v3/drafts/workitems/${workItemId}/attachments/type-list`
    : `/api/v3/workitems/${workItemId}/attachments/type-list`

const useWorkItemAttachmentTypeList: (workItemId?: number, draft?: boolean) => WorkItemAttachmentsTypeResult = (workItemId?: number, draft?: boolean) => {
    const [result, setResult] = useState<IAttachmentTypeListItem[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        const controller = new AbortController();

        const fetchWorkItemAttachmentsList = async () => {
            if (user && user.id_token && workItemId) {
                setLoading(true);
                try {
                    const urlRoot = makeAttachmentTypeListUrl(workItemId, draft);
                    const resp = await fetcher(urlRoot, {
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const response = await resp.json() as { attachments: WorkItemAttachmentType[] };

                    let groups = response.attachments.map((type) => type.groupName).filter((value, index, self) => self.indexOf(value) === index).sort();
                    let result: Array<IAttachmentTypeListItem> = [];

                    groups && groups.length > 0 && groups?.forEach(group => {
                        result.push({ id: Math.floor(Math.random()), name: group, isGroupHeader: true, isSensitive: false });
                        response.attachments && response.attachments.length > 0 && response.attachments.filter((f) => f.groupName == group).forEach((type) => (
                            result.push({ id: type.typeId, name: type.typeName, isGroupHeader: false, isSensitive: type.isSensitive, fileRequired: type.fileRequired, linkRequired: type.linkRequired })));
                    });

                    result.sort((a, b) => a.name.localeCompare(b.name));

                    setResult(result);
                } catch (err) {
                    // Will be done in BFLY-8658 or BFLY-9022
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            }
        };

        fetchWorkItemAttachmentsList();
        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [user, workItemId]);

    return [
        result,
        loading
    ];
};

export default useWorkItemAttachmentTypeList;